import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { prospectFormSchema, PROSPECT_STAGES, STAGE_LABELS } from '../../lib/validation';
import type { ProspectFormData } from '../../lib/validation';

interface Prospect {
  id: string;
  company_name: string;
  industry?: string;
  company_size?: string;
  website?: string;
  stage: string;
  source?: string;
  source_detail?: string;
  notes?: string;
  lost_reason?: string;
}

interface ProspectFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tournamentId: string;
  prospect?: Prospect | null;
}

const SOURCE_LABELS: Record<string, string> = {
  referral: 'Referral',
  cold_outreach: 'Cold Outreach',
  inbound: 'Inbound',
  event: 'Event',
  social: 'Social Media',
  other: 'Other',
};

const SIZE_LABELS: Record<string, string> = {
  startup: 'Startup',
  sme: 'SME',
  'mid-market': 'Mid-Market',
  enterprise: 'Enterprise',
  unknown: 'Unknown',
};

export default function ProspectForm({ isOpen, onClose, onSuccess, tournamentId, prospect }: ProspectFormProps) {
  const isEditing = !!prospect;

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<ProspectFormData>({
    resolver: zodResolver(prospectFormSchema),
    defaultValues: {
      stage: 'lead',
    }
  });

  const source = watch('source');

  useEffect(() => {
    if (prospect) {
      reset({
        company_name: prospect.company_name,
        industry: prospect.industry || '',
        company_size: (prospect.company_size as ProspectFormData['company_size']) || undefined,
        website: prospect.website || '',
        stage: (prospect.stage as ProspectFormData['stage']) || 'lead',
        source: (prospect.source as ProspectFormData['source']) || undefined,
        source_detail: prospect.source_detail || '',
        notes: prospect.notes || '',
        lost_reason: prospect.lost_reason || '',
      });
    } else {
      reset({ stage: 'lead' });
    }
  }, [prospect, reset, isOpen]);

  const onSubmit = async (data: ProspectFormData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (isEditing && prospect) {
        const { error } = await supabase
          .from('prospects')
          .update({
            company_name: data.company_name,
            industry: data.industry || null,
            company_size: data.company_size || null,
            website: data.website || null,
            stage: data.stage,
            source: data.source || null,
            source_detail: data.source_detail || null,
            notes: data.notes || null,
            lost_reason: data.lost_reason || null,
          })
          .eq('id', prospect.id);
        if (error) throw error;

        if (data.stage !== prospect.stage) {
          await supabase.from('prospect_activities').insert([{
            prospect_id: prospect.id,
            activity_type: 'stage_change',
            title: `Stage changed to ${STAGE_LABELS[data.stage as keyof typeof STAGE_LABELS]}`,
            metadata: { from_stage: prospect.stage, to_stage: data.stage },
            performed_by: user?.id,
          }]);
        }

        toast.success('Prospect updated');
      } else {
        const { data: newProspect, error } = await supabase
          .from('prospects')
          .insert([{
            company_name: data.company_name,
            industry: data.industry || null,
            company_size: data.company_size || null,
            website: data.website || null,
            stage: data.stage,
            source: data.source || null,
            source_detail: data.source_detail || null,
            notes: data.notes || null,
            tournament_id: tournamentId || null,
            created_by: user?.id,
          }])
          .select()
          .single();
        if (error) throw error;

        await supabase.from('prospect_activities').insert([{
          prospect_id: newProspect.id,
          activity_type: 'prospect_created',
          title: `Prospect created: ${data.company_name}`,
          metadata: { stage: data.stage, source: data.source },
          performed_by: user?.id,
        }]);

        toast.success('Prospect added');
      }

      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save prospect');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <Dialog.Title className="text-lg font-semibold text-golf-gray">
                    {isEditing ? 'Edit Prospect' : 'Add Prospect'}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input {...register('company_name')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="e.g. Acme Corporation" />
                    {errors.company_name && <p className="mt-1 text-xs text-red-600">{errors.company_name.message}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                      <input {...register('industry')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="e.g. Banking, Telecom" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                      <select {...register('company_size')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange bg-white">
                        <option value="">Select size</option>
                        {Object.entries(SIZE_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input {...register('website')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="https://example.com" />
                    {errors.website && <p className="mt-1 text-xs text-red-600">{errors.website.message}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Stage</label>
                      <select {...register('stage')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange bg-white">
                        {PROSPECT_STAGES.map(s => (
                          <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                      <select {...register('source')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange bg-white">
                        <option value="">Select source</option>
                        {Object.entries(SOURCE_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {source && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source Detail</label>
                      <input {...register('source_detail')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="e.g. Referred by John Doe" />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea {...register('notes')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange resize-none" placeholder="Any initial notes about this prospect..." />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-60">
                      {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Prospect'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
